import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Grid, IconButton, Tooltip, Avatar, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tabs, Tab,
} from '@mui/material';
import {
  Add, ArrowBack, Search, Groups, Person, Delete,
  Visibility, ChildCare, Refresh, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import dayjs from 'dayjs';

export default function Activities() {
  const [view, setView]       = useState('grid');
  const [selected, setSelected] = useState(null);
  const [tab, setTab]         = useState(0); // 0=Add, 1=View

  // Students list
  const [students, setStudents]           = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [searchStudent, setSearchStudent] = useState('');

  // Per-student expanded activities (view tab)
  const [expanded, setExpanded]           = useState({}); // { studentId: bool }
  const [studentActivities, setStudentActivities] = useState({}); // { studentId: [] }
  const [loadingActivities, setLoadingActivities] = useState({}); // { studentId: bool }

  // Add dialog
  const [dialog, setDialog]               = useState(false);
  const [dialogMode, setDialogMode]       = useState('all');
  const [targetStudent, setTargetStudent] = useState(null);
  const [saving, setSaving]               = useState(false);
  const [lastResult, setLastResult]       = useState(null);
  const [form, setForm]                   = useState({ title: '', type: '', details: '' });
  const [image, setImage]                 = useState(null);
  const [imagePath, setImagePath]         = useState('');
  const [uploading, setUploading]         = useState(false);

  // View detail dialog
  const [viewItem, setViewItem]           = useState(null);

  // ── Load students for selected class ──────────────────────────────────────
  const loadStudents = useCallback(async (cls) => {
    setStudentsLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getStudentsByStdSecAcYear, {
      standard: cls.standard, section: cls.section, acYear: cls.acYear,
    });
    setStudents(data?.students || []);
    setStudentsLoading(false);
  }, []);

  const handleClassSelect = useCallback(async (cls) => {
    setSelected(cls);
    setView('students');
    setLastResult(null);
    setTab(0);
    setExpanded({});
    setStudentActivities({});
    await loadStudents(cls);
  }, [loadStudents]);

  // ── Load activities for a specific student (by student_id) ────────────────
  const loadStudentActivities = async (studentId) => {
    setLoadingActivities(p => ({ ...p, [studentId]: true }));
    const data = await InvokeApi(Config.main_url + apiList.getKidsActivity, {
      id: studentId, status: 'Active',
    });
    setStudentActivities(p => ({ ...p, [studentId]: data?.activities || [] }));
    setLoadingActivities(p => ({ ...p, [studentId]: false }));
  };

  const toggleExpand = async (studentId) => {
    const isOpen = expanded[studentId];
    setExpanded(p => ({ ...p, [studentId]: !isOpen }));
    if (!isOpen && !studentActivities[studentId]) {
      await loadStudentActivities(studentId);
    }
  };

  // ── Delete activity by ID ─────────────────────────────────────────────────
  const handleDelete = async (activityId, studentId) => {
    if (!window.confirm('Delete this activity?')) return;
    const res = await InvokeApi(Config.main_url + apiList.updateKidsActivity, { id: activityId });
    if (res?.code === '200') {
      setStudentActivities(p => ({
        ...p,
        [studentId]: (p[studentId] || []).filter(a => a.ID !== activityId),
      }));
      if (viewItem?.ID === activityId) setViewItem(null);
    } else {
      alert(res?.message || 'Failed to delete');
    }
  };

  // ── Image upload ──────────────────────────────────────────────────────────
  const handleImageUpload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('kids_activity', file);
    const res = await postImg(Config.main_url + apiList.addActivityImage, fd);
    if (res?.code === '200') setImagePath(res.imageName || '');
    setUploading(false);
  };

  // ── Open add dialog ───────────────────────────────────────────────────────
  const openDialog = (mode, student = null) => {
    setDialogMode(mode);
    setTargetStudent(student);
    setForm({ title: '', type: '', details: '' });
    setImage(null);
    setImagePath('');
    setLastResult(null);
    setDialog(true);
  };

  // ── Save activity ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title) { alert('Activity name is required'); return; }
    setSaving(true);
    let data;
    if (dialogMode === 'all') {
      // Add for all students in class — backend loops through students by std/sec
      data = await InvokeApi(Config.main_url + apiList.addKidsActivityByStdSec, {
        standard: selected.standard, section: selected.section,
        type: form.type || '', title: form.title,
        details: form.details || '', kids_activity: imagePath || '', createdBy: '1',
      });
    } else {
      // Add for single student by student_id
      data = await InvokeApi(Config.main_url + apiList.addKidsActivity, {
        studentId: targetStudent.ID,
        type: form.type || '', title: form.title,
        details: form.details || '', kids_activity: imagePath || '', createdBy: '1',
      });
    }
    setSaving(false);
    if (data?.code === '200') {
      setLastResult({
        success: true, mode: dialogMode,
        studentName: targetStudent?.full_name,
        total: data.total_students, added: data.successful_activities,
      });
      setDialog(false);
      // Refresh activities for affected student(s)
      if (dialogMode === 'student' && targetStudent) {
        await loadStudentActivities(targetStudent.ID);
        setExpanded(p => ({ ...p, [targetStudent.ID]: true }));
      } else {
        // Refresh all expanded students
        for (const sid of Object.keys(expanded)) {
          if (expanded[sid]) await loadStudentActivities(sid);
        }
      }
    } else {
      alert(data?.message || 'Failed');
    }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const filteredStudents = students.filter(s =>
    !searchStudent || s.full_name?.toLowerCase().includes(searchStudent.toLowerCase())
  );

  // ── Grid view ──────────────────────────────────────────────────────────────
  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChildCare sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#1a237e">Kids Activities </Typography>
              <Typography variant="body2" color="text.secondary">Add and manage activities per student</Typography>
            </Box>
          </Box>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleClassSelect} />
        </Card>
      </Box>
    );
  }

  // ── Students view ──────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChildCare sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Kids Activities </Typography>
            <Typography variant="body2" color="text.secondary">
              {selected?.standard} - {selected?.section} · {selected?.acYear} · {students.length} students
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Button variant="contained" startIcon={<Groups />} onClick={() => openDialog('all')}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add for All
          </Button>
        </Box>
      </Box>

      {/* Success banner */}
      {lastResult?.success && (
        <Card sx={{ borderRadius: 3, mb: 2, p: 2, background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
          <Typography fontWeight={700} color="#2e7d32" fontSize={14}>✓ Activity added!</Typography>
          <Typography fontSize={13} color="#2e7d32">
            {lastResult.mode === 'all'
              ? `Added for ${lastResult.added} of ${lastResult.total} students`
              : `Added for ${lastResult.studentName}`}
          </Typography>
        </Card>
      )}

      {/* Search */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <TextField size="small" placeholder="Search student..." value={searchStudent}
            onChange={e => setSearchStudent(e.target.value)} sx={{ width: 300 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </CardContent>
      </Card>

      {/* Students table with expandable activity rows */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {studentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Student', 'Roll No', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>No students found</TableCell></TableRow>
                ) : filteredStudents.map((s, i) => {
                  const isExpanded = !!expanded[s.ID];
                  const acts = studentActivities[s.ID] || [];
                  const isLoading = !!loadingActivities[s.ID];
                  return (
                    <React.Fragment key={s.ID}>
                      {/* Student row */}
                      <TableRow hover sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary', width: 40 }}>{i + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src={s.profile_pic}
                              sx={{ width: 34, height: 34, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                              {s.full_name?.[0]}
                            </Avatar>
                            <Typography fontWeight={600} fontSize={13}>{s.full_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{s.roll_number || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Add Activity for this student">
                              <Button size="small" variant="outlined" startIcon={<Add />}
                                onClick={() => openDialog('student', s)}
                                sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontWeight: 600, fontSize: 11 }}>
                                Add
                              </Button>
                            </Tooltip>
                            <Tooltip title={isExpanded ? 'Hide Activities' : 'View Activities'}>
                              <Button size="small" variant="outlined"
                                startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                onClick={() => toggleExpand(s.ID)}
                                sx={{ borderRadius: 2, color: '#1565c0', borderColor: '#1565c0', fontWeight: 600, fontSize: 11 }}>
                                {isExpanded ? 'Hide' : 'View'}
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded activities sub-rows */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ p: 0, background: '#f8f9ff' }}>
                            {isLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} sx={{ color: '#1a237e' }} />
                              </Box>
                            ) : acts.length === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Typography fontSize={13} color="text.secondary">No activities for {s.full_name}</Typography>
                              </Box>
                            ) : (
                              <Box sx={{ px: 3, py: 1.5 }}>
                                <Typography fontSize={12} fontWeight={700} color="#1a237e" mb={1}>
                                  {acts.length} activit{acts.length !== 1 ? 'ies' : 'y'} for {s.full_name}
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ background: '#e8eaf6' }}>
                                      {['Activity', 'Type', 'Image', 'Date', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12, py: 0.8 }}>{h}</TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {acts.map(a => (
                                      <TableRow key={a.ID} hover>
                                        <TableCell sx={{ py: 1 }}>
                                          <Typography fontWeight={600} fontSize={12}>{a.title}</Typography>
                                          {a.details && (
                                            <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{a.details}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                          {a.type ? <Chip label={a.type} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10 }} /> : '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                          {a.kids_activity ? (
                                            <Box component="img"
                                              src={a.kids_activity_url || a.kids_activity}
                                              alt={a.title}
                                              sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: 'cover', border: '1px solid #e8eaf6' }}
                                            />
                                          ) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap', py: 1 }}>
                                          {a.created_at ? dayjs(a.created_at).format('DD MMM YYYY') : '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                          <Tooltip title="View">
                                            <IconButton size="small" onClick={() => setViewItem({ ...a, studentName: s.full_name })} sx={{ color: '#1a237e' }}>
                                              <Visibility sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDelete(a.ID, s.ID)} sx={{ color: '#c62828' }}>
                                              <Delete sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ── Add Activity Dialog ── */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dialogMode === 'all' ? <Groups sx={{ color: '#1a237e' }} /> : <Person sx={{ color: '#1a237e' }} />}
            {dialogMode === 'all'
              ? `Add Activity — All Students (${selected?.standard} - ${selected?.section}) `
              : `Add Activity — ${targetStudent?.full_name}`}
          </Box>
          <Typography fontSize={12} color="text.secondary" fontWeight={400} mt={0.5}>
            {dialogMode === 'all'
              ? 'Stored per student_id for each student in this class'
              : `Student ID: ${targetStudent?.ID} · Roll: ${targetStudent?.roll_number || '—'}`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth sx={{ borderRadius: 2 }} disabled={uploading}>
                {uploading ? 'Uploading...' : image ? `✓ ${image.name}` : 'Upload Activity Image (optional)'}
                <input type="file" hidden accept="image/*" onChange={e => {
                  const file = e.target.files[0];
                  if (file) { setImage(file); handleImageUpload(file); }
                }} />
              </Button>
              {image && !uploading && (
                <Box component="img" src={URL.createObjectURL(image)} alt="preview"
                  sx={{ mt: 1, width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 2 }} />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Activity Name *" value={form.title} onChange={f('title')} autoFocus />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Activity Type" value={form.type} onChange={f('type')} placeholder="e.g. Art, Sports, Music, Dance" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Activity Details" value={form.details} onChange={f('details')} multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || uploading}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 140 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (dialogMode === 'all' ? 'Send to All' : 'Add Activity')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Detail Dialog ── */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewItem && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {viewItem.title}
              {viewItem.type && <Chip label={viewItem.type} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />}
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={viewItem.studentName || '—'} size="small" sx={{ background: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                {viewItem.created_at && (
                  <Chip label={dayjs(viewItem.created_at).format('DD MMM YYYY')} size="small" sx={{ background: '#f5f5f5', color: '#666', fontWeight: 600 }} />
                )}
              </Box>
              {viewItem.kids_activity && (
                <Box component="img"
                  src={a.kids_activity_url || a.kids_activity}
                  alt={viewItem.title}
                  sx={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 2, mb: 2 }}
                />
              )}
              {viewItem.details ? (
                <Box sx={{ p: 2, background: '#f5f6fa', borderRadius: 2, borderLeft: '4px solid #1a237e' }}>
                  <Typography fontSize={13} color="#333" lineHeight={1.8} sx={{ whiteSpace: 'pre-wrap' }}>{viewItem.details}</Typography>
                </Box>
              ) : (
                <Typography fontSize={13} color="text.secondary">No details provided.</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0', gap: 1 }}>
              <Button onClick={() => { handleDelete(viewItem.ID, viewItem.student_id); }}
                variant="contained" startIcon={<Delete />}
                sx={{ background: '#c62828', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#b71c1c' } }}>
                Delete
              </Button>
              <Button onClick={() => setViewItem(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
